import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import type { Layer, LeafletMouseEvent, PathOptions } from 'leaflet';
import type { Feature, Geometry, GeoJsonObject } from 'geojson';
import { useApp } from '../context/AppContext';
import { createColorScale } from '../utils/colors';
import { Legend } from './Legend';
import { formatNaira, formatPercent } from '../utils/formatters';

// Component to handle map center and zoom
function MapController() {
  const map = useMap();
  
  useEffect(() => {
    // Center on Nigeria
    map.setView([9.0820, 8.6753], 6);
  }, [map]);
  
  return null;
}

export function MapView() {
  const { 
    state, 
    data, 
    geoFeaturesWithData, 
    selectState, 
    setHoveredState,
    currentYearSummary,
  } = useApp();
  
  const mapRef = useRef<L.Map | null>(null);
  const geoJsonRef = useRef<L.GeoJSON | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  
  // Get tooltip data for hovered state
  const tooltipData = useMemo(() => {
    if (!state.hoveredState || !currentYearSummary || !data) return null;
    
    const ranking = currentYearSummary.rankings.find(r => r.state === state.hoveredState);
    if (!ranking) return null;
    
    const stateSummary = data.stateSummaries.get(state.hoveredState);
    const previousYearSummary = data.yearSummaries.get(state.selectedYear - 1);
    const previousRanking = previousYearSummary?.rankings.find(r => r.state === state.hoveredState);
    
    // Calculate year-over-year change
    let yoyChange: number | null = null;
    if (previousRanking && previousRanking.total > 0) {
      yoyChange = ((ranking.total - previousRanking.total) / previousRanking.total) * 100;
    }
    
    return {
      state: state.hoveredState,
      rank: ranking.rank,
      total: ranking.total,
      percentOfTotal: ranking.percentOfTotal,
      yoyChange,
      allTimeTotal: stateSummary?.allTimeTotal ?? 0,
    };
  }, [state.hoveredState, state.selectedYear, currentYearSummary, data]);
  
  // Color scale
  const colorScale = useMemo(() => {
    if (!currentYearSummary) return () => '#1e293b';
    const values = currentYearSummary.rankings.map(r => r.total);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return createColorScale(min, max, state.highContrastMode ? 'highContrast' : 'default');
  }, [currentYearSummary, state.highContrastMode]);
  
  // Style function for GeoJSON - use generic Feature type
  const getFeatureStyle = useCallback((feature?: Feature<Geometry>): PathOptions => {
    if (!feature || !feature.properties) return {};
    
    const allocation = (feature.properties.allocation as number) ?? 0;
    const stateName = feature.properties.admin1Name as string;
    const isSelected = state.selectedStates.includes(stateName);
    const isHovered = state.hoveredState === stateName;
    
    return {
      fillColor: colorScale(allocation),
      fillOpacity: isSelected ? 0.9 : isHovered ? 0.8 : 0.7,
      color: isSelected ? '#22d3ee' : isHovered ? '#67e8f9' : '#334155',
      weight: isSelected ? 3 : isHovered ? 2 : 1,
      opacity: 1,
    };
  }, [colorScale, state.selectedStates, state.hoveredState]);
  
  // Event handlers
  const onEachFeature = useCallback((feature: Feature<Geometry>, layer: Layer) => {
    const stateName = feature.properties?.admin1Name as string;
    if (!stateName) return;
    
    layer.on({
      click: () => {
        selectState(stateName);
      },
      mouseover: (e: LeafletMouseEvent) => {
        setHoveredState(stateName);
        // Get position relative to map container
        if (mapContainerRef.current) {
          const rect = mapContainerRef.current.getBoundingClientRect();
          setTooltipPosition({ 
            x: e.originalEvent.clientX - rect.left, 
            y: e.originalEvent.clientY - rect.top 
          });
        }
        const target = e.target as L.Path;
        target.setStyle({
          weight: 2,
          color: '#67e8f9',
          fillOpacity: 0.8,
        });
        target.bringToFront();
      },
      mousemove: (e: LeafletMouseEvent) => {
        // Get position relative to map container
        if (mapContainerRef.current) {
          const rect = mapContainerRef.current.getBoundingClientRect();
          setTooltipPosition({ 
            x: e.originalEvent.clientX - rect.left, 
            y: e.originalEvent.clientY - rect.top 
          });
        }
      },
      mouseout: (e: LeafletMouseEvent) => {
        setHoveredState(null);
        setTooltipPosition(null);
        if (geoJsonRef.current) {
          geoJsonRef.current.resetStyle(e.target);
        }
      },
    });
  }, [selectState, setHoveredState]);
  
  // Min/max for legend
  const { min, max } = useMemo(() => {
    if (!currentYearSummary) return { min: 0, max: 0 };
    const values = currentYearSummary.rankings.map(r => r.total);
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [currentYearSummary]);

  // GeoJSON data object
  const geoJsonData: GeoJsonObject = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: geoFeaturesWithData,
  }), [geoFeaturesWithData]);
  
  if (!data) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-surface-400">Loading map...</div>
      </div>
    );
  }
  
  return (
    <div ref={mapContainerRef} className="relative h-full w-full">
      <MapContainer
        ref={mapRef}
        center={[9.0820, 8.6753]}
        zoom={6}
        className="h-full w-full rounded-xl"
        zoomControl={true}
        attributionControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        dragging={true}
      >
        <MapController />
        <GeoJSON
          ref={geoJsonRef}
          data={geoJsonData}
          style={getFeatureStyle}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
      
      {/* Legend overlay */}
      <div className="absolute bottom-4 left-4 z-[1000]">
        <Legend min={min} max={max} title={`${state.selectedYear} Allocation`} />
      </div>
      
      {/* Year badge */}
      <div className="absolute top-4 right-4 z-[1000]">
        <div className="badge-accent text-lg font-display font-bold px-4 py-2">
          {state.selectedYear}
        </div>
      </div>
      
      {/* Tooltip */}
      {tooltipData && tooltipPosition && mapContainerRef.current && (() => {
        const containerWidth = mapContainerRef.current.offsetWidth;
        const containerHeight = mapContainerRef.current.offsetHeight;
        const tooltipWidth = 180;
        const tooltipHeight = 140;
        const offset = 10;
        
        // Calculate position - default to bottom-right of cursor
        let left = tooltipPosition.x + offset;
        let top = tooltipPosition.y + offset;
        
        // Flip to left if would overflow right
        if (left + tooltipWidth > containerWidth - 10) {
          left = tooltipPosition.x - tooltipWidth - offset;
        }
        
        // Flip to top if would overflow bottom
        if (top + tooltipHeight > containerHeight - 10) {
          top = tooltipPosition.y - tooltipHeight - offset;
        }
        
        // Clamp to container bounds
        left = Math.max(10, Math.min(left, containerWidth - tooltipWidth - 10));
        top = Math.max(10, Math.min(top, containerHeight - tooltipHeight - 10));
        
        return (
          <div 
            className="absolute z-[1001] pointer-events-none"
            style={{ left, top }}
          >
            <div className="bg-surface-900/95 backdrop-blur-md border border-surface-600/80 rounded-lg shadow-2xl px-2.5 py-2 w-[180px]">
              {/* State Name & Rank */}
              <div className="flex items-center justify-between gap-2 mb-1.5 pb-1.5 border-b border-surface-600/60">
                <span className="font-display font-bold text-white text-xs truncate">
                  {tooltipData.state}
                </span>
                <span className="text-[10px] font-semibold text-accent-400 bg-accent-400/10 px-1.5 py-0.5 rounded shrink-0">
                  #{tooltipData.rank}
                </span>
              </div>
              
              {/* Stats */}
              <div className="space-y-0.5 text-[11px]">
                {/* Allocation */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-surface-400">{state.selectedYear}</span>
                  <span className="font-semibold text-white">{formatNaira(tooltipData.total, true)}</span>
                </div>
                
                {/* Percent of Total */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-surface-400">Share</span>
                  <span className="font-medium text-surface-300">{tooltipData.percentOfTotal.toFixed(1)}%</span>
                </div>
                
                {/* Year-over-Year Change */}
                {tooltipData.yoyChange !== null && (
                  <div className="flex items-center justify-between gap-2 pt-1 mt-0.5 border-t border-surface-700/60">
                    <span className="text-surface-400">vs {state.selectedYear - 1}</span>
                    <span className={`font-semibold ${
                      tooltipData.yoyChange > 0 
                        ? 'text-emerald-400' 
                        : tooltipData.yoyChange < 0 
                          ? 'text-rose-400' 
                          : 'text-surface-400'
                    }`}>
                      {formatPercent(tooltipData.yoyChange)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
